import { useState, useEffect, useRef } from 'react';
import { PaperPlaneRight, Robot, ArrowsClockwise } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { sendAiGameMessage, resetAiGame } from '../../lib/games';
import { toast } from 'sonner';

export function AiChatGame({ gameType, opener, quickReplies = [] }) {
  const [sessionId] = useState(() => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [messages, setMessages] = useState([{ role: 'host', text: opener }]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setMessages((m) => [...m, { role: 'player', text: message }]);
    setInput('');
    setBusy(true);
    try {
      const reply = await sendAiGameMessage(gameType, sessionId, message);
      setMessages((m) => [...m, { role: 'host', text: reply }]);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'The AI host is unreachable right now');
    } finally {
      setBusy(false);
    }
  };

  const restart = async () => {
    await resetAiGame(gameType, sessionId).catch(() => {});
    setMessages([{ role: 'host', text: opener }]);
  };

  return (
    <div className="space-y-3" data-testid={`game-ai-${gameType}`}>
      <div className="h-72 overflow-y-auto space-y-3 p-3 rounded-lg border border-white/10 bg-black/50" data-testid="ai-game-transcript">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === 'player' ? 'justify-end' : ''}`}>
            {m.role === 'host' && <Robot size={18} className="text-amber-300 shrink-0 mt-1" />}
            <p className={`text-sm leading-relaxed max-w-[80%] px-3 py-2 rounded-lg ${
              m.role === 'player' ? 'bg-amber-400/15 text-amber-100' : 'bg-white/5'
            }`}>
              {m.text}
            </p>
          </div>
        ))}
        {busy && <p className="text-xs text-muted-foreground" data-testid="ai-game-typing">AI host is thinking...</p>}
        <div ref={endRef} />
      </div>

      {quickReplies.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {quickReplies.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => send(q)}
              disabled={busy}
              className="text-xs px-3 py-1.5 rounded-full border border-amber-400/40 text-amber-200 hover:bg-amber-400/10 transition-colors disabled:opacity-40"
              data-testid={`ai-game-quick-${q.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {q}
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Input
          placeholder="Type your move..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          disabled={busy}
          data-testid="ai-game-input"
        />
        <Button onClick={() => send()} disabled={busy} className="rounded-full" data-testid="ai-game-send">
          <PaperPlaneRight size={16} />
        </Button>
      </div>

      <Button variant="outline" className="rounded-full w-full" onClick={restart} data-testid="ai-game-restart">
        <ArrowsClockwise size={14} className="mr-2" /> Start over
      </Button>
    </div>
  );
}
